import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, Button, Typography, Container, Box, TextField } from '@mui/material';
import TopBar from '../components/TopBar';
import { getApiUrl } from '../apiUrl';
import { supabase, isSupabaseConfigured } from '../supabaseClient';

const API_URL = getApiUrl();

const SignUp = () => {
  const [username, setUsername] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const completeProfile = async () => {
    setLoading(true);
    try {
      if (!supabase) {
        throw new Error('Supabase env vars are missing. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_PUBLISHABLE_KEY in client/.env and restart npm start.');
      }
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (!token) throw new Error('Please sign in with Google first.');

      await axios.post(`${API_URL}/api/profile/bootstrap`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (zipCode.length === 5) {
        const geo = await axios.get(`${API_URL}/api/geocode/zip`, { params: { zip: zipCode } });
        const [lng, lat] = geo.data.coordinates;
        await axios.post(`${API_URL}/update-profile`, {
          username: username.trim() || undefined,
          zipCode,
          coordinates: [lng, lat],
          mapCenter: { type: 'Point', coordinates: [lng, lat] },
          mapZoom: 10.5,
          notificationRadius: 10
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else if (username.trim()) {
        await axios.post(`${API_URL}/update-profile`, {
          username: username.trim()
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      navigate('/dashboard');
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert('Signup setup failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      if (!supabase) {
        throw new Error('Supabase env vars are missing. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_PUBLISHABLE_KEY in client/.env and restart npm start.');
      }
      const redirectTo = `${window.location.origin}/signup`;
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo }
      });
      if (error) throw error;
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert('Google sign-in failed: ' + (error.message || 'Unknown error'));
    }
  };

  useEffect(() => {
    const maybeBootstrap = async () => {
      if (!supabase) return;
      const { data } = await supabase.auth.getSession();
      if (data?.session?.access_token) {
        await completeProfile();
      }
    };
    maybeBootstrap();
  }, []);

  return (
    <>
      <TopBar />
      <Container component="main" maxWidth="sm">
        <Card sx={{ mt: 8, p: 2 }}>
          <CardContent>
            <Typography component="h1" variant="h5" align="center" gutterBottom>
              Join Clearance Scouts
            </Typography>
            {!isSupabaseConfigured && (
              <Typography color="error" sx={{ mb: 1 }}>
                Supabase config missing. Set `REACT_APP_SUPABASE_URL` and `REACT_APP_SUPABASE_PUBLISHABLE_KEY` in `client/.env`, then restart.
              </Typography>
            )}
            <TextField
              margin="normal"
              fullWidth
              label="Username (optional now)"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <TextField
              margin="normal"
              fullWidth
              label="Zip Code (optional now)"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={completeProfile}
                disabled={loading}
                sx={{ backgroundColor: '#202E40' }}
              >
                {loading ? 'Please wait...' : 'Finish setup'}
              </Button>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleGoogleLogin}
                sx={{
                  backgroundColor: '#4285f4',
                  color: 'white',
                  '&:hover': { backgroundColor: '#357ae8' },
                  textTransform: 'none',
                  py: 1.5
                }}
              >
                Continue with Google
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </>
  );
};

export default SignUp;
